'use client';

import React, { Component, ReactNode } from 'react';
import { LuTriangleAlert, LuRefreshCw, LuBug } from 'react-icons/lu';

import { logger } from '../utils';

import { Box } from './box';
import { VStack } from './stack';
import { Text } from './text';
import { Button } from './button';
import { Alert } from './alert';

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorCount: number;
}

export interface ErrorBoundaryProps {
  /**
   * Children components to be wrapped
   */
  children: ReactNode;
  /**
   * Custom fallback component to render on error
   */
  fallback?: (error: Error, errorInfo: React.ErrorInfo, retry: () => void) => ReactNode;
  /**
   * Callback when an error occurs
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /**
   * Whether to show error details in development
   */
  showDetails?: boolean;
  /**
   * Maximum number of retries before showing permanent error
   */
  maxRetries?: number;
  /**
   * Custom error message
   */
  errorMessage?: string;
  /**
   * Custom retry button text
   */
  retryText?: string;
  /**
   * Whether to log errors to console
   */
  logToConsole?: boolean;
  /**
   * Whether to send error reports to backend
   */
  reportToBackend?: boolean;
  /**
   * API endpoint for error reporting
   */
  errorReportEndpoint?: string;
  /**
   * Additional metadata to include in error reports
   */
  metadata?: Record<string, unknown>;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { onError, logToConsole = true, reportToBackend = false } = this.props;

    // Log using proper logger in development
    if (logToConsole && process.env.NODE_ENV === 'development') {
      logger.error('ErrorBoundary caught an error', error, { errorInfo });
    }

    // Call custom error handler
    if (onError) {
      onError(error, errorInfo);
    }

    // Report to backend
    if (reportToBackend) {
      this.reportError(error, errorInfo);
    }

    // Update state with error info
    this.setState((prevState) => ({
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));
  }

  reportError = async (error: Error, errorInfo: React.ErrorInfo) => {
    const { errorReportEndpoint = '/api/v1/errors', metadata = {} } = this.props;

    try {
      const errorReport = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
          userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
          url: typeof window !== 'undefined' ? window.location.href : 'unknown',
        },
      };

      // Only send if we're in a browser environment
      if (typeof window !== 'undefined') {
        await fetch(errorReportEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(errorReport),
        });
      }
    } catch (reportError) {
      logger.error(
        'Failed to report error',
        reportError instanceof Error ? reportError : new Error(String(reportError))
      );
    }
  };

  handleReset = () => {
    const { maxRetries = 3 } = this.props;

    if (this.state.errorCount >= maxRetries) {
      logger.warn(`Maximum retries (${maxRetries}) reached. Not retrying.`);
      return;
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { hasError, error, errorInfo, errorCount } = this.state;
    const {
      children,
      fallback,
      showDetails = process.env.NODE_ENV === 'development',
      maxRetries = 3,
      errorMessage = 'Bir şeyler yanlış gitti',
      retryText = 'Tekrar dene',
    } = this.props;

    if (hasError && error && errorInfo) {
      // Use custom fallback if provided
      if (fallback) {
        return <>{fallback(error, errorInfo, this.handleReset)}</>;
      }

      const canRetry = errorCount < maxRetries;

      // Default error UI
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          padding={8}
          width="100%"
          backgroundColor="#F8F9FA"
        >
          <VStack gap={6} maxWidth="600px" width="100%">
            {/* Error Alert */}
            <Alert.Root status="error">
              <Alert.Indicator>
                <LuTriangleAlert />
              </Alert.Indicator>
              <VStack alignItems="flex-start" gap={2} flex="1">
                <Alert.Title>{errorMessage}</Alert.Title>
                <Alert.Description>
                  {error.message ||
                    'Beklenmeyen bir hata oluştu. Lütfen daha sonra tekrar deneyin.'}
                </Alert.Description>
              </VStack>
            </Alert.Root>

            {/* Error Details (Development Only) */}
            {showDetails && (
              <Box
                width="100%"
                padding={4}
                backgroundColor="#FFF5F5"
                borderRadius="md"
                border="1px solid #FED7D7"
              >
                <VStack alignItems="stretch" gap={3}>
                  <Text fontWeight="semibold" color="#E53E3E">
                    <Box as="span" display="inline" marginRight="8px">
                      <LuBug />
                    </Box>
                    Error Details (Development Only)
                  </Text>

                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="#742A2A">
                      Error Message:
                    </Text>
                    <Text fontSize="xs" fontFamily="mono" color="#9B2C2C">
                      {error.toString()}
                    </Text>
                  </Box>

                  {error.stack && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="#742A2A">
                        Stack Trace:
                      </Text>
                      <Box
                        as="pre"
                        fontSize="xs"
                        fontFamily="mono"
                        color="#9B2C2C"
                        overflowX="auto"
                        padding={2}
                        backgroundColor="#FFF5F5"
                        borderRadius="sm"
                        whiteSpace="pre-wrap"
                        wordBreak="break-word"
                      >
                        {error.stack}
                      </Box>
                    </Box>
                  )}

                  {errorInfo.componentStack && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="#742A2A">
                        Component Stack:
                      </Text>
                      <Box
                        as="pre"
                        fontSize="xs"
                        fontFamily="mono"
                        color="#9B2C2C"
                        overflowX="auto"
                        padding={2}
                        backgroundColor="#FFF5F5"
                        borderRadius="sm"
                        whiteSpace="pre-wrap"
                        wordBreak="break-word"
                      >
                        {errorInfo.componentStack}
                      </Box>
                    </Box>
                  )}
                </VStack>
              </Box>
            )}

            {/* Actions */}
            <VStack gap={2}>
              {canRetry ? (
                <Button variant="primary" onClick={this.handleReset} leftIcon={<LuRefreshCw />}>
                  {retryText}
                </Button>
              ) : (
                <Text fontSize="sm" color="#E53E3E">
                  Maximum retry attempts reached. Please refresh the page.
                </Text>
              )}

              {errorCount > 0 && canRetry && (
                <Text fontSize="xs" color="#6C757D">
                  Retry attempt {errorCount} of {maxRetries}
                </Text>
              )}
            </VStack>
          </VStack>
        </Box>
      );
    }

    return children;
  }
}

export { ErrorBoundary };

// Export with display name for debugging
(ErrorBoundary as unknown as React.ComponentType<{ children?: React.ReactNode }>).displayName =
  'ErrorBoundary';

// Helper hook for error handling
export function useErrorHandler() {
  return (error: Error, _errorInfo?: { componentStack?: string }) => {
    // This will be caught by the nearest ErrorBoundary
    throw error;
  };
}

// HOC for wrapping components with ErrorBoundary
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
}
